// MACHINERY V2 (Stroje) — fleet data layer (M2.2).
//
// Everything FleetV1Page READS: the loads, the one batch telemetry call, and
// every derivation on top of them. Split out of the page so the page is
// composition + handlers only, and both stay under the 300-line cap.
//
// DATA PARITY (§5 #9). The loads below are the live grid's, unchanged:
// getMachines(query) / getTypes / fetchGroups / getDecommissionedMachines
// against the SAME `customerMachinery` slice. The fetch is NOT trimmed — every
// MachineViewModel property still arrives, including the ones nothing renders
// (they feed Duplicate and the edit dialogs). Nothing is mirrored into
// machineryV2UiSlice, which stays UI-only.
//
// FILTER SPLIT (changed at M2.2 review F3 / ruling B(ii) — no longer the live
// grid's split):
//   search / groupBy                          -> MachinesQuery (server-side)
//   type / manufacturer / location / 9 states -> CLIENT-side facets
// The fetch therefore sends only `{search, groupBy}`, and every facet count is
// computed over that one unfiltered result set. See fleetSelectors.ts's header
// for the full reasoning; the endpoint has no pagination, so the client holds
// the complete set. Transport strategy is not data parity — every field is
// still fetched and still bound.
//
// STATUS FILTERS ONLY APPLY WHERE TELEMETRY EXISTS (review F4). The
// decommissioned scope fetches no telemetry, so every machine there derives
// `unconnected`; leaving an active-scope status filter applied would render
// "no decommissioned machines" while decommissioned machines plainly exist, and
// the strip that would reveal the filter is hidden in that scope. So the filter
// is IGNORED there and the page shows a note — never silently cleared, never
// silently matching nothing.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine, MachineType } from "../../models/models";
import {
  changeQuery,
  getDecommissionedMachines,
  getMachines,
  getTypes,
} from "../../customerMachinerySlice";
import { fetchGroups } from "../../machineGroupsSlice";
import {
  MachineryV2ReadinessPresentation,
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import { MachineryV2CardState, MachineryV2Telemetry } from "./cardStates";
import { isGatewayLive, useFleetTelemetry } from "./fleetTelemetry";
import {
  FleetGroups,
  FleetLocationNode,
  applyClientFilters,
  buildLocationTree,
  countStates,
  derivedManufacturers,
  deriveFleetStates,
  filterDecommissioned,
  flattenGroups,
  groupMachinesClientSide,
} from "./fleetSelectors";

export interface FleetPageData {
  /** DISTINCT machines in the current scope + search — the FACET base set. */
  flat: Machine[];
  types: MachineType[];
  /**
   * THE sections both views render, grouped CLIENT-SIDE by the slice's
   * `fleetGroupBy` (M2.3 ruling (i) — one grouping key, not two).
   *
   * Computed here, once, so Fleet V1's cards and Fleet V2's rows can never
   * section the same fleet differently, and so changing the dimension never
   * re-fetches.
   *
   * A machine that belongs to several groups (`line` / `department` key on the
   * `groupNames` ARRAY) appears in EVERY section it belongs to — that is
   * correct, a machine in two lines really is in both. It is still ONE machine
   * in every count, because every count derives from `visibleFlat` / `flat`,
   * which `flattenGroups` de-duplicates by id.
   */
  sections: FleetGroups;
  /** The filtered base set, flattened + de-duplicated. Feeds `sections` and
   *  every count. */
  visibleFlat: Machine[];
  visibleCount: number;
  telemetry: Map<string, MachineryV2Telemetry>;
  states: Map<string, MachineryV2CardState>;
  stateCounts: Record<string, number>;
  connectedCount: number;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  typeCounts: Record<string, number>;
  locations: FleetLocationNode[];
  manufacturers: { id: string; name: string }[];
  activeCount: number;
  decommissionedCount: number;
  isLoading: boolean;
  /** True when the user has status filters set but the current scope has no
   *  telemetry to apply them to — the page renders a note (review F4). */
  statusFiltersIgnored: boolean;
}

export const useFleetPageData = (scope: "active" | "decommissioned"): FleetPageData => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const { query, machines, types, loading, decommissionedMachines, decommissionedLoading } =
    useAppSelector((s) => s.customerMachinery);
  const { fleetStatusFilters, fleetLocationFilter, fleetGroupBy } = useAppSelector(
    (s) => s.machineryV2Ui
  );

  useEffect(() => {
    dispatch(getTypes());
    dispatch(fetchGroups());
    dispatch(getDecommissionedMachines());
    // Read-only, and now display-inert (see the fetchQuery note): it only
    // keeps the request shape identical to the live grid's. v2 NEVER writes
    // this key — `fleetGroupBy` is what v2 sections by.
    const savedGroupBy = localStorage.getItem("machinery_groupBy");
    if (savedGroupBy) dispatch(changeQuery({ groupBy: savedGroupBy }));
    return () => {
      dispatch(changeQuery({}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ONLY the server-side half of the query. Changing a facet (type /
  // manufacturer) must NOT re-fetch, or the facet counts would collapse back to
  // the filtered set — which is the F3 bug.
  //
  // NOTE FOR THE NEXT READER: `groupBy` is still SENT (the fetch is unchanged
  // on purpose), but since M2.3 ruling (i) it no longer decides anything the
  // user sees. All it does is shape the response into Record<groupName, …>,
  // and `flattenGroups` immediately flattens that away; the sections both views
  // render come from the slice's `fleetGroupBy` instead. Do not "fix" a
  // grouping bug by changing this param — it is inert for display.
  const fetchQuery = useMemo(
    () => ({ search: query.search, groupBy: query.groupBy }),
    [query.search, query.groupBy]
  );

  useEffect(() => {
    dispatch(getMachines(fetchQuery));
  }, [dispatch, fetchQuery]);

  useEffect(() => {
    if (scope === "decommissioned") dispatch(getDecommissionedMachines());
  }, [dispatch, scope]);

  // The decommissioned scope is filtered + grouped client-side (its endpoint
  // returns a flat list). SEARCH ONLY here — type/manufacturer are facets and
  // are applied below, so this stays the facet BASE set.
  //
  // The grouping in this step is NOT the display grouping: like the server's
  // response shape for the active scope, it is flattened away by `visibleFlat`
  // and re-grouped by `fleetGroupBy` at `sections`. It exists only so both
  // scopes hand `applyClientFilters` the same Record<name, Machine[]> shape.
  const decommissionedGroups = useMemo(
    () =>
      scope === "decommissioned"
        ? groupMachinesClientSide(
            filterDecommissioned(decommissionedMachines, { search: query.search }),
            query.groupBy || "type"
          )
        : {},
    [scope, decommissionedMachines, query.search, query.groupBy]
  );

  const sourceGroups: FleetGroups =
    scope === "decommissioned" ? decommissionedGroups : machines || {};
  const flat = useMemo(() => flattenGroups(sourceGroups), [sourceGroups]);

  // ONE batch telemetry read per page. The decommissioned scope renders no live
  // surface, so it passes no ids and the request is skipped entirely.
  const liveIds = useMemo(
    () => (scope === "decommissioned" ? [] : flat.map((m) => m.id)),
    [scope, flat]
  );
  const telemetry = useFleetTelemetry(liveIds);

  const states = useMemo(() => deriveFleetStates(flat, telemetry), [flat, telemetry]);
  const stateCounts = useMemo(() => countStates(flat, states), [flat, states]);

  // Telemetry only exists in the active scope (see the header note).
  const statusFiltersIgnored =
    scope === "decommissioned" && fleetStatusFilters.length > 0;

  const visibleGroups = useMemo(
    () =>
      applyClientFilters(sourceGroups, states, {
        statusFilters: scope === "decommissioned" ? [] : fleetStatusFilters,
        locationFilter: fleetLocationFilter,
        typeId: query.typeId,
        manufacturerId: query.manufacturerId,
      }),
    [
      sourceGroups,
      states,
      scope,
      fleetStatusFilters,
      fleetLocationFilter,
      query.typeId,
      query.manufacturerId,
    ]
  );

  const summaryStrings = useMemo(() => readinessSummaryStrings(translate), [translate]);
  const readiness = useMemo(() => {
    const map = new Map<string, MachineryV2ReadinessPresentation>();
    for (const m of flat) {
      // Custom machines (no catalog model) and decommissioned machines carry no
      // readiness surface on the live grid — the SAME gate, not a new one.
      if (!m.catalogModel || m.isDecommissioned) continue;
      const t = telemetry.get(m.id);
      map.set(
        m.id,
        deriveReadinessPresentation(
          {
            readiness: m.catalogModel.readiness,
            isOemPartner: m.catalogModel.isOemPartner,
            oemPartnerName: m.catalogModel.oemPartnerName,
          },
          { connected: t ? isGatewayLive(t) : false },
          summaryStrings
        )
      );
    }
    return map;
  }, [flat, telemetry, summaryStrings]);

  const typeCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const m of flat) {
      const name = m.type?.name || "Unknown";
      map[name] = (map[name] || 0) + 1;
    }
    return map;
  }, [flat]);

  const visibleFlat = useMemo(() => flattenGroups(visibleGroups), [visibleGroups]);

  // ONE grouping, for BOTH views (ruling (i)).
  const sections = useMemo(
    () => groupMachinesClientSide(visibleFlat, fleetGroupBy),
    [visibleFlat, fleetGroupBy]
  );
  const locations = useMemo(() => buildLocationTree(flat), [flat]);
  const manufacturers = useMemo(
    () => derivedManufacturers(flat, query.typeId),
    [flat, query.typeId]
  );
  const activeCount = useMemo(() => flattenGroups(machines).length, [machines]);

  return {
    flat,
    types,
    sections,
    visibleFlat,
    visibleCount: visibleFlat.length,
    telemetry,
    states,
    stateCounts,
    connectedCount: flat.length - (stateCounts.unconnected || 0),
    readiness,
    typeCounts,
    locations,
    manufacturers,
    activeCount,
    decommissionedCount: decommissionedMachines.length,
    isLoading: scope === "decommissioned" ? decommissionedLoading : loading,
    statusFiltersIgnored,
  };
};

export default useFleetPageData;
